import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentSuitabilityData,
  type EnrollmentSuitabilitySubmitInput,
  type EnrollmentSuitabilitySubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentSuitabilityData>
 * @example const response = await getEnrollmentSuitabilityData()
 */
export const getEnrollmentSuitabilityData = async (context?: DefaultContext): Promise<EnrollmentSuitabilityData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentSuitabilityData',
      query: `mutation getEnrollmentSuitabilityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                investment {
                  isBrokerDealer
                  brokerDealerEmployer
                  isDirectorPolicyMakerOfCompany
                  directorPolicyMakerOfCompany
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentSuitabilityData }
  return response.submitEnrollment
}

export const useGetEnrollmentSuitabilityData = createQueryHook<undefined, EnrollmentSuitabilityData>(
  getEnrollmentSuitabilityData,
)

/**
 * Submit Enrollment Suitability data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentSuitabilitySubmitOutput>
 * @example const response = await submitEnrollmentSuitabilityData();
 */
export const submitEnrollmentSuitabilityData = async (
  enrollmentSuitabilitySubmitInput: EnrollmentSuitabilitySubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentSuitabilitySubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentSuitabilityData',
      query: `mutation submitEnrollmentSuitabilityData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentSuitabilitySubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentSuitabilitySubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentSuitabilityData = createMutationHook<
  EnrollmentSuitabilitySubmitInput,
  EnrollmentSuitabilitySubmitOutput
>(submitEnrollmentSuitabilityData)
